import React from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import { Subscription, SubscriptionPlan } from '../../types/subscription';
import { getPlanById } from '../../constants/subscriptionPlans';
import { SubscriptionUtils } from '../../utils/subscriptionUtils';

interface SubscriptionCardProps {
  subscription: Subscription;
  className?: string;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  subscription,
  className = ''
}) => {
  const { language, strings } = useLocalization();
  const plan = getPlanById(subscription.planId);
  
  if (!plan) {
    return (
      <div className={`p-6 bg-red-50 border border-red-200 rounded-lg ${className}`}>
        <p className="text-red-600">{language === 'ar' ? 'خطأ: لم يتم العثور على خطة الاشتراك' : 'Error: Subscription plan not found'}</p>
      </div>
    );
  }

  const isInTrial = SubscriptionUtils.isInTrialPeriod(subscription);
  const trialDaysRemaining = SubscriptionUtils.getTrialDaysRemaining(subscription);
  const isActive = SubscriptionUtils.isActive(subscription);
  const isExpired = SubscriptionUtils.isExpired(subscription);
  
  const getStatusColor = () => {
    if (isExpired) return 'bg-red-100 text-red-800 border-red-200';
    if (isInTrial) return 'bg-blue-100 text-blue-800 border-blue-200';
    if (subscription.status === 'cancelled') return 'bg-gray-100 text-gray-800 border-gray-200';
    return 'bg-green-100 text-green-800 border-green-200';
  };

  const getStatusText = () => {
    if (isExpired) return strings.subscriptions.expired;
    if (isInTrial) return strings.subscriptions.trial;
    if (subscription.status === 'cancelled') return strings.subscriptions.cancelled;
    return strings.subscriptions.active;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(parseInt(dateString));
    return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getPlanIcon = () => {
    switch (plan.icon) {
      case 'gift': return '🎁';
      case 'star': return '⭐';
      case 'crown': return '👑';
      default: return '📋';
    }
  };

  const getPlanColor = () => {
    switch (plan.color) {
      case 'gray': return 'text-gray-600';
      case 'blue': return 'text-blue-600';
      case 'gold': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className={`text-2xl ${getPlanColor()}`}>
              {getPlanIcon()}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{language === 'ar' ? plan.nameAr : plan.name}</h3>
              <p className="text-sm text-gray-600">{language === 'ar' ? plan.descriptionAr : plan.description}</p>
            </div>
          </div>
          
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor()}`}>
            {getStatusText()}
          </div>
        </div>
      </div>

      {/* Plan Details */}
      <div className="p-6 space-y-4">
        {/* Pricing */}
        <div className="flex items-baseline space-x-2">
          <span className="text-3xl font-bold text-gray-900">
            {plan.isFreeTier ? (language === 'ar' ? 'مجاني' : 'Free') : `${plan.price} EGP`}
          </span>
          {!plan.isFreeTier && (
            <span className="text-sm text-gray-600">/{language === 'ar' ? (plan.billingCycle === 'monthly' ? 'شهرياً' : 'سنوياً') : plan.billingCycle}</span>
          )}
        </div>

        {/* Trial Info */}
        {isInTrial && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <span className="text-blue-600">🎉</span>
              <div>
                <p className="text-sm font-medium text-blue-800">{strings.subscriptions.freeTrial}</p>
                <p className="text-xs text-blue-600">
                  {trialDaysRemaining} {strings.subscriptions.daysLeft}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Dates */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">{strings.subscriptions.startDate}:</span>
            <span className="font-medium">{formatDate(subscription.startDate)}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">{strings.subscriptions.endDate}:</span>
            <span className="font-medium">{formatDate(subscription.endDate)}</span>
          </div>
        </div>


      </div>

    </div>
  );
};

export default SubscriptionCard;
